import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Paginator from '../../components/ui/Paginator';
import Message from '../../components/ui/Message';
import { costingApi, formatCost, formatMoney, formatQuantity, kindLabel, REVERSIBLE_KINDS } from './costing';

const PAGE_SIZE = 20;

const signedQuantity = (m: any) => {
    if (m.kind === 'AJUSTE_COSTO') return 0;
    const q = Number(m.newQuantity) - Number(m.previousQuantity);
    return Number.isFinite(q) ? q : 0;
};

const csvCell = (value: any) => `"${String(value ?? '').replace(/"/g, '""')}"`;

// Kardex (RF-11): una línea por movimiento del producto, con costo unitario, valor y saldos
export const KardexPage = () => {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const id = Number(params.get('id'));

    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [page, setPage] = useState(1);
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [isPortalAdmin, setIsPortalAdmin] = useState(false);
    const [reversing, setReversing] = useState<any | null>(null);
    const [costDialog, setCostDialog] = useState(false);
    const [newCost, setNewCost] = useState('');
    const [costReason, setCostReason] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const load = async (p: number) => {
        setLoading(true);
        const resp = await costingApi.kardex(id, { page: p, pageSize: PAGE_SIZE, startDate: startDate || undefined, endDate: endDate || undefined });
        setLoading(false);
        if (resp.data) setData(resp.data);
        else setError(resp.error!);
    };

    useEffect(() => { costingApi.configuration().then((r) => setIsPortalAdmin(!!r.data?.isPortalAdmin)); }, []);
    useEffect(() => { if (id) load(page); }, [id, page, startDate, endDate]);

    const exportCsv = async () => {
        const rows: any[] = [];
        for (let p = 1; p <= 50; p++) {
            const resp = await costingApi.kardex(id, { page: p, pageSize: 500, startDate: startDate || undefined, endDate: endDate || undefined });
            if (!resp.data) return setError(resp.error!);
            rows.push(...resp.data.movements);
            if (p >= resp.data.totalPages) break;
        }
        const headers = ['Fecha', 'Tipo', 'Documento', 'Entrada', 'Salida', 'Costo unitario', 'Valor', 'Saldo cantidad', 'Saldo valor', 'Costo promedio', 'Nota'];
        const lines = rows.slice().reverse().map((m) => {
            const q = signedQuantity(m);
            return [
                m.date ? new Date(m.date).toLocaleString() : '', kindLabel(m.kind, m.movementType),
                m.documentType ? `${m.documentType} ${m.documentId ?? ''}` : (m.invoiceNumber ? `FACTURA ${m.invoiceNumber}` : ''),
                q > 0 ? q : '', q < 0 ? -q : '', m.unitCost ?? '', m.value ?? '', m.balanceQuantity ?? m.newQuantity, m.balanceValue ?? '', m.averageCost ?? '', m.note ?? '',
            ].map(csvCell).join(',');
        });
        const blob = new Blob(['﻿' + [headers.map(csvCell).join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.setAttribute('download', `kardex_${data?.product?.code ?? id}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const reverse = async (reason?: string) => {
        const movement = reversing;
        setReversing(null);
        const resp = await costingApi.reverse(movement.id, reason ?? '');
        if (resp.error) return setError(resp.error);
        setSuccess('Se revirtió el movimiento.');
        load(page);
    };

    const saveCost = async () => {
        if (newCost === '' || Number(newCost) < 0) return setError('Indique un costo válido.');
        if (!costReason.trim()) return setError('Indique el motivo del ajuste de costo.');
        const resp = await costingApi.adjustCost(id, Number(newCost), costReason.trim());
        if (resp.error) return setError(resp.error);
        setCostDialog(false);
        setNewCost('');
        setCostReason('');
        setSuccess('Se ajustó el costo del producto.');
        load(1);
        setPage(1);
    };

    const product = data?.product;

    return (
        <div className='mt-5 container-fluid px-4' style={{ flex: 1, marginTop: 10 }}>
            <div className="d-flex flex-wrap justify-content-between align-items-start mb-4">
                <div>
                    <h3 className="mb-1">Kardex{product ? `: ${product.name}` : ''}</h3>
                    <p className="text-muted mb-0">{product ? `${product.code} · ` : ''}Movimientos con su costo y saldos.</p>
                </div>
                <div className="d-flex gap-2 mt-2 mt-md-0">
                    <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/stocks')}>
                        <i className="bi bi-arrow-left me-1" /> Volver al inventario
                    </button>
                    {isPortalAdmin && product && (
                        <button type="button" className="btn btn-outline-primary" onClick={() => { setNewCost(String(product.averageCost ?? '')); setCostDialog(true); }}>
                            <i className="bi bi-pencil me-1" /> Ajustar costo
                        </button>
                    )}
                    <button type="button" className="btn btn-outline-secondary" onClick={exportCsv} disabled={!data || data.totalRecords === 0}>
                        <i className="bi bi-upload me-1" /> Exportar
                    </button>
                </div>
            </div>

            {product && (
                <div className="row g-3 mb-3">
                    {[
                        { label: 'Existencia', value: `${formatQuantity(product.quantity)} ${product.unit ?? ''}` },
                        { label: 'Valor del inventario', value: formatMoney(product.value) },
                        { label: 'Costo promedio', value: formatCost(product.averageCost) },
                        { label: 'Último costo de compra', value: product.lastCost === null || product.lastCost === undefined ? '-' : formatCost(product.lastCost) },
                    ].map((c) => (
                        <div className="col-6 col-md-3" key={c.label}>
                            <div className="card border-0 shadow-sm h-100"><div className="card-body">
                                <div className="text-muted small">{c.label}</div>
                                <div className={`fs-5 fw-semibold ${c.label === 'Existencia' && Number(product.quantity) < 0 ? 'text-danger' : ''}`}>{c.value}</div>
                            </div></div>
                        </div>
                    ))}
                </div>
            )}

            <div className="card border-0 shadow-sm">
                <div className="card-body">
                    <div className="row g-2 mb-3">
                        <div className="col-md-3">
                            <label htmlFor="kardexStart" className="form-label small text-muted mb-1">Desde</label>
                            <input id="kardexStart" type="date" className="form-control" value={startDate} onChange={(e) => { setStartDate(e.target.value); setPage(1); }} />
                        </div>
                        <div className="col-md-3">
                            <label htmlFor="kardexEnd" className="form-label small text-muted mb-1">Hasta</label>
                            <input id="kardexEnd" type="date" className="form-control" value={endDate} onChange={(e) => { setEndDate(e.target.value); setPage(1); }} />
                        </div>
                    </div>
                    <div className="table-responsive">
                        <table className="table table-hover align-middle small">
                            <thead>
                                <tr className="text-muted text-uppercase">
                                    <th>Fecha</th><th>Tipo</th><th>Documento</th>
                                    <th className="text-end">Entrada</th><th className="text-end">Salida</th>
                                    <th className="text-end">Costo unitario</th><th className="text-end">Valor</th>
                                    <th className="text-end">Saldo</th><th className="text-end">Saldo valor</th><th className="text-end">Promedio</th>
                                    <th>Nota</th><th />
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan={12} className="text-center py-4"><span className="spinner-border spinner-border-sm me-2" /> Cargando...</td></tr>
                                ) : !data || data.movements.length === 0 ? (
                                    <tr><td colSpan={12} className="text-center py-4 text-muted">No hay movimientos.</td></tr>
                                ) : data.movements.map((m: any) => {
                                    const q = signedQuantity(m);
                                    const canReverse = REVERSIBLE_KINDS.includes(m.kind) && !m.reversed;
                                    return (
                                        <tr key={m.id} className={m.reversed ? 'text-decoration-line-through text-muted' : ''}>
                                            <td className="text-nowrap">{m.date ? new Date(m.date).toLocaleString() : '-'}</td>
                                            <td>{kindLabel(m.kind, m.movementType)}</td>
                                            <td>{m.documentType ? `${m.documentType === 'FACTURA' ? 'Factura' : m.documentType === 'COMPRA' ? 'Compra' : m.documentType === 'TRASLADO' ? 'Traslado' : 'Conteo'} ${m.documentId ?? ''}` : m.invoiceNumber ? `Factura ${m.invoiceNumber}` : '-'}</td>
                                            <td className="text-end text-success">{q > 0 ? formatQuantity(q) : ''}</td>
                                            <td className="text-end text-danger">{q < 0 ? formatQuantity(-q) : ''}</td>
                                            <td className="text-end">{m.unitCost === null || m.unitCost === undefined ? '-' : formatCost(m.unitCost)}</td>
                                            <td className="text-end">{m.value === null || m.value === undefined ? '-' : formatMoney(m.value)}</td>
                                            <td className={`text-end ${Number(m.balanceQuantity ?? m.newQuantity) < 0 ? 'text-danger' : ''}`}>{formatQuantity(m.balanceQuantity ?? m.newQuantity)}</td>
                                            <td className="text-end">{m.balanceValue === null || m.balanceValue === undefined ? '-' : formatMoney(m.balanceValue)}</td>
                                            <td className="text-end">{m.averageCost === null || m.averageCost === undefined ? '-' : formatCost(m.averageCost)}</td>
                                            <td className="text-muted">{m.note ?? '-'}</td>
                                            <td className="text-end">
                                                {canReverse && (
                                                    <button type="button" className="btn btn-sm btn-outline-secondary" title="Revertir movimiento" onClick={() => setReversing(m)}>
                                                        <i className="bi bi-arrow-counterclockwise" />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    {data && data.totalPages > 1 && <Paginator totalPage={data.totalPages} pageActual={page} onChange={(p) => setPage(p)} />}
                </div>
            </div>

            {reversing && (
                <Message type="info" title="Revertir movimiento" showReasonField={true} reasonLabel="Motivo" showCancel={true} okText="Revertir"
                    message={`Se registrará el movimiento contrario de "${kindLabel(reversing.kind)}" con su mismo costo. Indique el motivo.`}
                    closeDialog={() => setReversing(null)} handleCancel={() => setReversing(null)} handleOk={reverse} backgroundColor="rgba(0,0,0,0.4)" />
            )}

            {costDialog && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.4)' }} tabIndex={-1} role="dialog" aria-label="Ajustar costo">
                    <div className="modal-dialog"><div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Ajustar costo</h5>
                            <button type="button" className="btn-close" aria-label="Close" onClick={() => setCostDialog(false)} />
                        </div>
                        <div className="modal-body">
                            <p className="small text-muted">La existencia se revaloriza al nuevo costo y las salidas siguientes usan este promedio. Solo el administrador del portal puede hacerlo.</p>
                            <label htmlFor="newCostInput" className="form-label">Nuevo costo promedio *</label>
                            <input id="newCostInput" type="number" min="0" step="any" className="form-control mb-3" value={newCost} onChange={(e) => setNewCost(e.target.value)} />
                            <label htmlFor="costReasonInput" className="form-label">Motivo *</label>
                            <input id="costReasonInput" className="form-control" maxLength={300} value={costReason} onChange={(e) => setCostReason(e.target.value)} />
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-outline-secondary" onClick={() => setCostDialog(false)}>Cancelar</button>
                            <button type="button" className="btn btn-primary" onClick={saveCost}>Guardar</button>
                        </div>
                    </div></div>
                </div>
            )}

            {error && <Message type="error" message={error} closeDialog={() => setError('')} handleOk={() => setError('')} backgroundColor="rgba(0,0,0,0.4)" />}
            {success && <Message type="success" title="Exitoso" message={success} closeDialog={() => setSuccess('')} handleOk={() => setSuccess('')} backgroundColor="rgba(0,0,0,0.4)" />}
        </div>
    );
};
